import { Button, Form, FormInstance } from "antd";
import { useShowInputContext } from "context/ShowInputContext";
import { useInstallationAdditionalInformationUpdate } from "hooks/queries/useInstallation";
import Done from "pages/orders/Done";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BiPencil, BiSave } from "react-icons/bi";
import { RxCross2 } from "react-icons/rx";
import { InstallationSchema, OrderModelForm } from "types/installation";
import { cleanData, formattedDateTime } from "utils/global";
import Instruction from "./Instruction";
import WorkerNotice from "./WorkerNotice";

export default function WorkerTab({
  installation,
}: {
  installation: InstallationSchema;
}) {
  const { t } = useTranslation();
  const { showInput, setShowInput, setIsLoading } = useShowInputContext();
  const [form] = Form.useForm<OrderModelForm>();
  const formRef = useRef<FormInstance<OrderModelForm>>(null);

  const { mutateAsync, isLoading } = useInstallationAdditionalInformationUpdate(
    installation.id
  );

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    form.setFieldsValue({
      additionalSetupDoneDateTime:
        installation.additionalInformation?.additionalSetupDoneDateTime ??
        formattedDateTime(installation.setupDoneDateTime) ??
        "",
    });
  }, [installation]);

  const handleSaveInputs = () => {
    formRef.current?.submit();
  };

  const handleShowInputs = () => setShowInput(true);

  const onSubmit = async (data: any) => {
    const additionalInformation = {
      ...installation.additionalInformation,
      additionalSetupDoneDateTime: data.additionalSetupDoneDateTime,
    };

    await mutateAsync({
      additionalInformation: cleanData(additionalInformation as any),
    });

    setShowInput(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center gap-2">
        <h5 className="underline">{t("orders:modalTabs:worker")}</h5>
        {showInput ? (
          <div className="flex gap-2">
            <Button
              key={"save"}
              type="primary"
              onClick={handleSaveInputs}
              loading={isLoading}
            >
              <BiSave />
            </Button>
            <Button
              key={"cancel"}
              type="default"
              className="btn-outline"
              onClick={() => setShowInput(false)}
            >
              <RxCross2 />
            </Button>
          </div>
        ) : (
          <Button key={"edit"} type="primary" onClick={handleShowInputs}>
            <BiPencil />
          </Button>
        )}
      </div>
      <Form
        form={form}
        validateTrigger="onChange"
        ref={formRef}
        onFinish={onSubmit}
        autoComplete="off"
        className="flex flex-col gap-4"
      >
        <Done installation={installation} />
        <Instruction installation={installation} />
        <WorkerNotice installation={installation} />
      </Form>
    </div>
  );
}
